/**
 * 지식 베이스 패널
 * 지식 목록을 카테고리별로 그룹화하여 표시
 */

import React, { useState } from 'react';
import { useKnowledge } from '../../contexts/KnowledgeContext';
import KnowledgeEditor from './KnowledgeEditor';
import KnowledgeViewModal from './KnowledgeViewModal';
import './KnowledgePanel.css';

function KnowledgePanel() {
  const {
    filteredItems,
    groupedItems,
    loading,
    error,
    filters,
    stats,
    categories,
    updateFilters,
    deleteItem,
    toggleFavorite,
    toggleActive
  } = useKnowledge();

  const [expandedCategories, setExpandedCategories] = useState(
    Object.keys(categories)
  );
  const [editingItem, setEditingItem] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // 카테고리 토글
  const toggleCategory = (category) => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // 삭제 핸들러
  const handleDelete = async (itemId) => {
    try {
      await deleteItem(itemId);
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  // 에디터 닫기
  const handleEditorClose = () => {
    setEditingItem(null);
    setIsCreating(false);
  };

  return (
    <div className="knowledge-panel">
      {/* 헤더 */}
      <div className="knowledge-header">
        <h2>지식 베이스</h2>
        <button
          className="btn-add"
          onClick={() => setIsCreating(true)}
        >
          + 추가
        </button>
      </div>

      {/* 검색 및 필터 */}
      <div className="knowledge-filters">
        <input
          type="text"
          placeholder="검색..."
          value={filters.searchTerm}
          onChange={(e) => updateFilters({ searchTerm: e.target.value })}
          className="search-input"
        />
        <select
          value={filters.category}
          onChange={(e) => updateFilters({ category: e.target.value })}
          className="category-select"
        >
          <option value="">전체 카테고리</option>
          {Object.entries(categories).map(([key, cat]) => (
            <option key={key} value={key}>
              {cat.icon} {cat.label}
            </option>
          ))}
        </select>
        <label className="show-inactive">
          <input
            type="checkbox"
            checked={filters.showInactive}
            onChange={(e) => updateFilters({ showInactive: e.target.checked })}
          />
          비활성 표시
        </label>
      </div>

      {/* 에러 메시지 */}
      {error && <div className="error-message">{error}</div>}

      {/* 로딩 */}
      {loading && <div className="loading">로딩 중...</div>}

      {/* 지식 목록 */}
      <div className="knowledge-list">
        {Object.entries(categories).map(([categoryKey, category]) => {
          const categoryItems = groupedItems[categoryKey] || [];
          if (categoryItems.length === 0 && !filters.showInactive) return null;

          const isExpanded = expandedCategories.includes(categoryKey);

          return (
            <div key={categoryKey} className="knowledge-category">
              <div
                className="category-header"
                onClick={() => toggleCategory(categoryKey)}
                style={{ borderLeftColor: category.color }}
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-label">{category.label}</span>
                <span className="category-count">({categoryItems.length})</span>
                <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                  ▶
                </span>
              </div>

              {isExpanded && (
                <div className="category-items">
                  {categoryItems.length === 0 ? (
                    <div className="empty-category">항목이 없습니다</div>
                  ) : (
                    categoryItems.map(item => (
                      <KnowledgeItem
                        key={item.id}
                        item={item}
                        onView={() => setViewingItem(item)}
                        onEdit={() => setEditingItem(item)}
                        onDelete={() => setDeleteConfirm(item)}
                        onToggleFavorite={() => toggleFavorite(item.id)}
                        onToggleActive={() => toggleActive(item.id)}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 통계 */}
      <div className="knowledge-stats">
        총 {stats.total}개 | 활성 {stats.active}개 | 즐겨찾기 {stats.favorites}개
      </div>

      {/* 에디터 모달 */}
      {(isCreating || editingItem) && (
        <KnowledgeEditor
          item={editingItem}
          onClose={handleEditorClose}
        />
      )}

      {/* 보기 모달 */}
      {viewingItem && (
        <KnowledgeViewModal
          item={viewingItem}
          onClose={() => setViewingItem(null)}
          onEdit={() => {
            setViewingItem(null);
            setEditingItem(viewingItem);
          }}
        />
      )}

      {/* 삭제 확인 모달 */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="delete-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>삭제 확인</h3>
            <p>"{deleteConfirm.title}"을(를) 삭제하시겠습니까?</p>
            <div className="modal-actions">
              <button onClick={() => setDeleteConfirm(null)}>취소</button>
              <button
                className="btn-danger"
                onClick={() => handleDelete(deleteConfirm.id)}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 지식 항목 컴포넌트
 */
function KnowledgeItem({ item, onView, onEdit, onDelete, onToggleFavorite, onToggleActive }) {
  const { categories } = useKnowledge();
  const category = categories[item.category] || categories.general_notes;

  return (
    <div className={`knowledge-item ${!item.isActive ? 'inactive' : ''}`}>
      <div className="item-content" onClick={onView}>
        <div className="item-title">
          {item.isFavorite && <span className="favorite-badge">⭐</span>}
          {item.title}
        </div>
        <div className="item-preview">
          {item.content?.substring(0, 100)}
          {item.content?.length > 100 ? '...' : ''}
        </div>
        {item.tags && item.tags.length > 0 && (
          <div className="item-tags">
            {item.tags.map((tag, idx) => (
              <span key={idx} className="tag">{tag}</span>
            ))}
          </div>
        )}
      </div>
      <div className="item-actions">
        <button
          className="btn-icon"
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
          title={item.isFavorite ? '즐겨찾기 해제' : '즐겨찾기'}
        >
          {item.isFavorite ? '⭐' : '☆'}
        </button>
        <button
          className="btn-icon"
          onClick={(e) => { e.stopPropagation(); onToggleActive(); }}
          title={item.isActive ? '비활성화' : '활성화'}
        >
          {item.isActive !== false ? '✓' : '○'}
        </button>
        <button
          className="btn-icon"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          title="수정"
        >
          ✎
        </button>
        <button
          className="btn-icon btn-delete"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="삭제"
        >
          🗑
        </button>
      </div>
    </div>
  );
}

export default KnowledgePanel;
